import { FC } from 'react';
import { TiArrowSortedDown, TiArrowSortedUp } from 'react-icons/ti';

import _ from '../locale';
import { styled } from '../theme';
import FlexRow from './FlexRow';
import { default as SvgIcon, SvgIconProps } from './SvgIcon';

export const IconsContainer = styled(FlexRow)({
    '*:not(:last-child)': {
        marginRight: 20,
    },
});

const ArrowUpIcon = styled(TiArrowSortedUp)(({ theme }) => ({
    cursor: 'pointer',
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.primary2,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const ArrowDownIcon = styled(TiArrowSortedDown)(({ theme }) => ({
    cursor: 'pointer',
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.primary2,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const StyledIcon = styled(SvgIcon)<{ disabled?: boolean }>(({ theme, disabled }) => ({
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.primary2,
    },
    ':active': {
        color: theme.color.primary3,
    },
    ...(disabled && {
        color: theme.color.disabled,
        pointerEvents: 'none',
        cursor: 'not-allowed',
        ':hover, :focus': {
            color: theme.color.disabled,
        },
    }),
}));

export const ActionIcon = (props) => <StyledIcon size="lg" {...props} />;

const RedIcon = styled(ActionIcon)<{ disabled?: boolean }>(({ theme, disabled }) => ({
    width: 16,
    color: theme.color.danger1,
    ':hover, :focus': {
        color: theme.color.danger2,
    },
    ':active': {
        color: theme.color.danger3,
    },
    ...(disabled && {
        color: theme.color.disabled,
        ':hover, :focus': {
            color: theme.color.disabled,
        },
    }),
}));

type BaseActionProps = {
    ['data-testid']?: string;
    onClick?: () => void;
};

type Props = Partial<SvgIconProps> & BaseActionProps;

export const ActionUp: FC<BaseActionProps> = (props) => <ArrowUpIcon {...props} />;
export const ActionDown: FC<BaseActionProps> = (props) => <ArrowDownIcon {...props} />;
export const ActionEdit: FC<Props> = (props) => (
    <ActionIcon tooltip={_('tooltip_edit')} src="/icons/ic-edit.svg" {...props} />
);

export const ActionDelete: FC<Props> = (props) => (
    <RedIcon tooltip={_('delete_label')} src="/icons/ic-trash.svg" {...props} />
);

export const ActionShare: FC<Props> = (props) => (
    <ActionIcon tooltip={_('tooltip_share')} src="/icons/ic-share.svg" {...props} />
);

export const ActionLink: FC<Props> = (props) => (
    <ActionIcon tooltip={_('tooltip_open_link')} src="/icons/ic-link.svg" {...props} />
);

export const ActionCancel: FC<Props> = (props) => (
    <RedIcon tooltip={_('cancel_button')} src="/icons/ic-cross.svg" {...props} />
);

export const ActionDownload: FC<Props> = (props) => (
    <ActionIcon tooltip={_('tooltip_download')} src="/icons/ic-download.svg" {...props} />
);

export const ActionCopy: FC<Props> = (props) => (
    <ActionIcon tooltip={_('data_collection_tooltip_copy')} src="/icons/ic-copy.svg" {...props} />
);

export const ActionClose: FC<Props> = (props) => <RedIcon src="/icons/ic-cross.svg" {...props} />;

export const ActionImpersonate: FC<Props> = (props) => (
    <ActionIcon
        style={{ transform: 'rotate(-90deg)' }}
        tooltip={_('tooltip_log_in_as')}
        src="/icons/ic-download.svg"
        {...props}
    />
);

export const ActionMail: FC<Props> = (props) => (
    <ActionIcon size="xl" src="/icons/ic-mail.svg" tooltip={_('email')} {...props} />
);

export const ActionDemote: FC<Props> = (props) => (
    <RedIcon src="/icons/ic-demote.svg" tooltip={_('tooltip_demote')} {...props} />
);

export const ActionPromote: FC<Props> = (props) => (
    <ActionIcon tooltip={_('tooltip_promote')} src="/icons/ic-promote.svg" {...props} />
);

export const ActionDownloadCSV: FC<Props> = (props) => (
    <ActionIcon tooltip={_('tooltip_download_csv')} src="/icons/ic-csv.svg" {...props} />
);
